import defaultAvatarUrl from '@/assets/images/default_avatar.png';
import { useDebounceFn } from 'ahooks';
import { Button, Checkbox, Empty, Input, Spin } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type {
  CheckboxOptionType,
  CheckboxValueType,
} from 'antd/es/checkbox/Group';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconFont } from '../IconFont';
import SZModal from '../common/SZModal';
import styles from './index.less';
import {
  DepartmentMemberOrgEnum,
  DepartmentType,
  SelectModalConfigType,
  SelectedMemberInfoType,
  UserType,
} from './interface';

/** 展示的头像信息 */
export const getAvatarUrl = (url?: string | null) => {
  if (!url || url === '-1') return defaultAvatarUrl;

  return url;
};

const CheckboxGroup = Checkbox.Group;
interface OneBreadcrumbType {
  key: string;
  value: string;
  label: string;
}
interface Props extends SelectModalConfigType {
  visible: boolean;
  departmentList: DepartmentType[];
  userList: UserType[];
  /** 默认选中的值 */
  defaultCheckedList?: SelectedMemberInfoType[];
  /** 取消/关闭回调 */
  onClose: () => void;
  /** 点击确定时候的回调 */
  onOk: (items: SelectedMemberInfoType[]) => void;
  /** 点击部门的回调 */
  onDepartmentChange: (departmentId: string) => void;
  /** 搜索框变化的回调 */
  onSearch: (value: string) => void;
  /** 加载中 */
  loading?: boolean;
}

const MemberSelectModal = (props: Props) => {
  const {
    visible = false,
    defaultCheckedList = [],
    onClose,
    onOk,
    modalTitle = '选择协作者',
    modalSelectedTip = '已选择：',
    isSingleSelected = false,
    departmentList = [],
    userList = [],
    onDepartmentChange,
    onSearch,
    loading,
  } = props;

  // 当前选则的部门id
  const [, setCurDeptId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  // 左侧面包屑，没有搜索词时展示
  const [breadcrumbList, setBreadcrumbList] = useState<OneBreadcrumbType[]>([
    {
      key: '0', // 顶级部门树传null
      value: '',
      label: '部门/用户',
    },
  ]);
  const { run: debouncedOnSearch } = useDebounceFn(onSearch, {
    wait: 700,
    leading: true,
    trailing: true,
  });
  // 可选择的成员列表
  const [datalistLoading, setDataListLoading] = useState<boolean>(false);
  const [indeterminate, setIndeterminate] = useState<boolean>(false);
  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [checkedList, setCheckedList] = useState<CheckboxValueType[]>([]);
  const [realCheckedList, setRealCheckedList] =
    useState<SelectedMemberInfoType[]>(defaultCheckedList);

  const resetPageData = useCallback(() => {
    setRealCheckedList([]);
    setDataListLoading(false);
    setIndeterminate(false);
    setCheckAll(false);
    setCheckedList([]);
  }, []);

  // 点击取消的操作
  const handleCancel = useCallback(() => {
    resetPageData();
    setRealCheckedList([]);
    onClose();
  }, [onClose]);

  // 源数据
  const DataListLen = userList.length + departmentList.length;

  // 左侧成员选项发生变化
  const onCheckGroupChange = useCallback(
    (list: CheckboxValueType[]) => {
      const isCheckedAll = list.length === DataListLen;
      const dataList = [...departmentList, ...userList];
      if (isCheckedAll) {
        // 在realCheckedList中增加，选中但未加入到realCheckedLis的数据
        const newRealCheckedList: SelectedMemberInfoType[] = [
          ...realCheckedList,
        ];
        dataList?.map((item) => {
          const findInRealChecedItemArr = realCheckedList.filter(
            (targetItem) => targetItem.key === item.key,
          );
          if (findInRealChecedItemArr?.length === 0) {
            newRealCheckedList.push({
              key: item.key,
              label: item.label,
              value: item.value,
              avatar: item?.avatar,
              type: item?.type,
            });
          }
          return item;
        });
        setRealCheckedList(newRealCheckedList);
      } else {
        // 在realCheckedList中增加选中的数据，在realCheckedList中删除该左侧列表中未选中的数据
        const newRealCheckedList1: SelectedMemberInfoType[] = [
          ...realCheckedList,
        ];
        dataList?.map((item: DepartmentType) => {
          const idInRealCheckedListIdx = realCheckedList.findIndex(
            (realListItem: SelectedMemberInfoType) =>
              realListItem.key === item.key,
          );
          if (list.includes(item.key)) {
            if (idInRealCheckedListIdx < 0) {
              // 增加该item
              newRealCheckedList1.push({
                key: item.key,
                label: item.label,
                value: item.value,
                avatar: item?.avatar,
                type: item?.type,
              });
            }
          } else if (idInRealCheckedListIdx >= 0) {
            newRealCheckedList1.splice(idInRealCheckedListIdx, 1);
          }
          return item;
        });
        setRealCheckedList(newRealCheckedList1);
      }
      setCheckedList(list);
      setIndeterminate(!!list.length && list.length < DataListLen);
      setCheckAll(isCheckedAll);
    },
    [DataListLen, realCheckedList, userList, isSingleSelected, departmentList],
  );

  // 更新源数据侧选中状态（注：不能加到useCallback中，否则会进入死循环）
  const updateSourceChecked = (hadCheckedList?: SelectedMemberInfoType[]) => {
    const curRealCheckedList: SelectedMemberInfoType[] =
      hadCheckedList || realCheckedList || [];
    // 把realCheckedList中的数据渲染到左侧树
    const list: CheckboxValueType[] = [];
    [...userList, ...departmentList]?.map((item: DepartmentType | UserType) => {
      const findInRealCheckedItems = curRealCheckedList.filter(
        (realItem: SelectedMemberInfoType) => item.key === realItem.key,
      );
      if (findInRealCheckedItems.length > 0) {
        list.push(item.key);
      }
      return item;
    });

    onCheckGroupChange(list);
  };

  // 左侧树源数据发生变化时，把右侧选中数据反写到左侧
  useEffect(() => {
    updateSourceChecked();
  }, [userList, departmentList]);

  /**
   * 点击"确定"的操作
   */
  const handleOk = useCallback(() => {
    onOk(realCheckedList);
  }, [realCheckedList, onOk]);

  // 用户信息元素
  const TenantInfoElem = useCallback(
    (
      item: DepartmentType | SelectedMemberInfoType,
      from: 'source' | 'target',
    ) => {
      const isChecked = realCheckedList.find(
        (cur) => cur.key === item.key || item.disabled === true,
      );

      return (
        <div className={styles['item-info']}>
          <div className={styles['item-info-left']}>
            {(item.type === DepartmentMemberOrgEnum.DEPARTMENT ||
              item.type === DepartmentMemberOrgEnum.ORG) && (
              <IconFont
                type="icon-bumen"
                style={{
                  fontSize: '26px',
                }}
              />
            )}
            {item.type === DepartmentMemberOrgEnum.USER && (
              <img
                src={getAvatarUrl(item?.avatar)}
                className={styles['icon']}
              />
            )}

            <div className={styles['name']}>
              {/* {from === 'target' ? item.label : item.value} */}
              {item.label || item.value}
            </div>
          </div>
          <div>
            {item.type === DepartmentMemberOrgEnum.DEPARTMENT &&
              !searchValue &&
              from !== 'target' && (
                <button
                  type="button"
                  style={{
                    color: `${isChecked ? 'grey' : '#4C7CFF'}`,
                    pointerEvents: `${isChecked ? 'none' : 'auto'}`,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isChecked) return;
                    if (item.disabled) return;
                    setCurDeptId(item.value);
                    // @ts-ignore
                    setBreadcrumbList((preState) => {
                      return [
                        ...preState,
                        {
                          key: item.key,
                          value: item.value,
                          label: item?.label || '',
                        },
                      ];
                    });
                    // @ts-ignore
                    onDepartmentChange(item.value);
                  }}
                >
                  下级
                </button>
              )}
          </div>
        </div>
      );
    },
    [breadcrumbList, realCheckedList, onDepartmentChange, searchValue],
  );

  // 把成员列表数据格式化为checkbox的options数据
  const formatDataToCheckboxOptionType = useCallback(
    (list: DepartmentType[]) => {
      const checkboxOptionTypeArr: CheckboxOptionType[] = [];
      list?.map((item) => {
        checkboxOptionTypeArr.push({
          label: TenantInfoElem(item, 'source'),
          value: item.value,
          disabled: item.disabled,
        });
        return item;
      });
      return checkboxOptionTypeArr;
    },
    [TenantInfoElem],
  );

  // 把部门数据格式化为checkbox的options数据
  const formatDataToCheckboxValueType = useCallback(
    (list: DepartmentType[]) => {
      const checkboxValueTypeArr: CheckboxValueType[] = [];
      list?.map((item) => {
        checkboxValueTypeArr.push(item.key);
        return item;
      });

      return checkboxValueTypeArr;
    },
    [],
  );

  // 全选按钮发生变更
  const onCheckAllChange = useCallback(
    (e: CheckboxChangeEvent) => {
      const dataList = [...departmentList, ...userList].filter(
        (item) => item.disabled !== true,
      );
      if (e.target.checked) {
        setCheckedList(formatDataToCheckboxValueType(dataList));
        // 在realCheckedList中增加未加入的数据
        const newRealCheckedList = [...realCheckedList];
        dataList?.map((item) => {
          const findInRealCheckedItemArr = realCheckedList.filter(
            (targetItem) => targetItem.key === item.key,
          );
          if (findInRealCheckedItemArr?.length === 0) {
            newRealCheckedList.push({
              key: item.key,
              label: item.label,
              value: item.value,
              avatar: item?.avatar,
              type: item?.type,
            });
          }
          return item;
        });
        setRealCheckedList(newRealCheckedList);
      } else {
        setCheckedList([]);
        // 在realCheckedList中删除左侧列表中未选中的数据
        const newRealCheckedList: SelectedMemberInfoType[] = [];
        realCheckedList?.map((item) => {
          const findInTendantListItemArr = dataList.filter(
            (sourceItem) => sourceItem.key === item.key,
          );
          if (findInTendantListItemArr?.length === 0) {
            newRealCheckedList.push({
              key: item.key,
              label: item.label,
              value: item.value,
              avatar: item?.avatar,
              type: item?.type,
            });
          }
          return item;
        });
        setRealCheckedList(newRealCheckedList);
      }
      setIndeterminate(false);
      setCheckAll(e.target.checked);
    },
    [userList, realCheckedList, departmentList],
  );

  const isButtonDisable = useMemo(() => {
    return (
      realCheckedList.length === 0 ||
      (isSingleSelected && realCheckedList.length !== 1)
    );
  }, [realCheckedList, isSingleSelected]);

  return (
    <SZModal
      centered={true}
      title={modalTitle}
      open={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      OKBtnDisable={isButtonDisable}
      maskClosable={true}
      destroyOnClose={true}
      width={660}
      className={`${styles['member-select-modal-content']}`}
      wrapClassName={`${styles['member-select-modal']}`}
    >
      <section className={styles['content']}>
        {/* 左侧 */}
        <div
          className={`${styles['content-col']} ${styles['content-source']} ${
            isSingleSelected && realCheckedList.length > 1
              ? styles['error']
              : ''
          }`}
        >
          <Spin spinning={datalistLoading || loading}>
            <div className={`${styles['header']} ${styles['source-header']}`}>
              <Input
                onChange={(e) => {
                  setSearchValue((e.target as HTMLInputElement)?.value);
                  debouncedOnSearch((e.target as HTMLInputElement)?.value);
                }}
                prefix={
                  <IconFont type="icon-mulusousuo" className={styles['icon']} />
                }
                type="text"
                placeholder="搜索用户/部门"
              />
              {!searchValue && (
                <div className={styles['breadcrumb']}>
                  {breadcrumbList?.map((item, index) => {
                    return (
                      <label
                        key={`breadcrumb${item.key}`}
                        className={styles['breadcrumb-item']}
                        onClick={() => {
                          setCurDeptId(item.value);
                          onDepartmentChange(item.value);
                          setBreadcrumbList((preState) => {
                            const idx = preState.findIndex(
                              (state) => state.key === item.key,
                            );
                            return preState.slice(0, idx + 1);
                          });
                        }}
                      >
                        {index !== 0 && (
                          <IconFont
                            type="icon-xiangyou"
                            className="text-icon-4 text-[16px] mx-1"
                          ></IconFont>
                        )}
                        <span className={styles['breadcrumb-title']}>
                          {item.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
              {DataListLen > 0 && (
                <div className={styles['checked-all']}>
                  <Checkbox
                    indeterminate={indeterminate}
                    onChange={onCheckAllChange}
                    checked={checkAll}
                  >
                    全选
                  </Checkbox>
                </div>
              )}
            </div>
            <div
              className={`${styles['list']} ${styles['source-list']}`}
              style={{ height: searchValue ? '374px' : '324px' }}
            >
              {!departmentList?.length && !userList?.length && (
                <div className={styles['source-list-empty']}>
                  <Empty />
                </div>
              )}
              {(!!userList?.length || !!departmentList?.length) && (
                <CheckboxGroup
                  className={styles['source-list-group']}
                  options={formatDataToCheckboxOptionType([
                    ...departmentList,
                    ...userList,
                  ])}
                  value={checkedList}
                  onChange={onCheckGroupChange}
                />
              )}
            </div>
          </Spin>
        </div>
        {/* 右侧 */}
        <div className={`${styles['content-col']} ${styles['content-target']}`}>
          <div className={`${styles['header']} ${styles['target-header']}`}>
            <span>
              {modalSelectedTip}
              {realCheckedList.length}人
            </span>
            {realCheckedList.length > 0 && (
              <Button
                onClick={() => {
                  onCheckGroupChange([]);
                  setRealCheckedList([]);
                }}
                className={styles['clear-all']}
              >
                清空
              </Button>
            )}
          </div>
          {realCheckedList.length > 0 && (
            <div
              style={{
                height: '400px',
              }}
              className={`${styles['list']} ${styles['target-list']}`}
            >
              {realCheckedList?.map((item, index) => {
                return (
                  <div key={item.key} className={styles['target-list-item']}>
                    {TenantInfoElem(item, 'target')}
                    <span
                      className={styles['delete']}
                      onClick={() => {
                        // 从realCheckedList中删除该条数据
                        const newRealCheckedList = [...realCheckedList];
                        newRealCheckedList.splice(index, 1);
                        updateSourceChecked(newRealCheckedList);
                        setRealCheckedList(newRealCheckedList);
                      }}
                    >
                      x
                      {/* <IconFont
                            type={'iconguanbi'}
                            className={styles['icon']}
                          /> */}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      {isSingleSelected && realCheckedList.length > 1 && (
        <div className={styles['tip-single-error']}>最多只能选择一个</div>
      )}
    </SZModal>
  );
};

export default MemberSelectModal;
